import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Crown, DollarSign, RefreshCw, Loader2, Search, CheckCircle2, Ban, Power } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

export default function PlatformCorporates() {
  const nav = useNavigate();
  const [corporates, setCorporates] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [pricingFor, setPricingFor] = useState(null);
  const [pricing, setPricing] = useState({ price: "", days: "" });
  const [savingPricing, setSavingPricing] = useState(false);
  const [deactFor, setDeactFor] = useState(null);
  const [savingDeact, setSavingDeact] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [k, s] = await Promise.all([api.get("/platform/corporate"), api.get("/platform/stats")]);
      setCorporates(k.data);
      setStats(s.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const defaultPrice = Number(stats?.monthly_price_usd ?? 29);

  const openPricing = (corp) => {
    setPricingFor(corp);
    setPricing({ price: String(corp.custom_monthly_price_usd || ""), days: "" });
  };

  const savePricing = async (e) => {
    e?.preventDefault();
    if (!pricingFor || savingPricing) return;
    setSavingPricing(true);
    try {
      await api.put(`/platform/corporate/${pricingFor.id}/pricing`, {
        custom_monthly_price_usd: Number(pricing.price) || 0,
        extend_paid_until_days: Number(pricing.days) || 0,
      });
      toast.success(`Updated pricing for ${pricingFor.name}`);
      setPricingFor(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to update");
    } finally {
      setSavingPricing(false);
    }
  };

  const saveDeact = async () => {
    if (!deactFor || savingDeact) return;
    const action = deactFor.deactivated ? "reactivate" : "deactivate";
    setSavingDeact(true);
    try {
      await api.post(`/platform/corporate/${deactFor.id}/${action}`);
      toast.success(`${deactFor.name} ${action}d`);
      setDeactFor(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || `Failed to ${action}`);
    } finally {
      setSavingDeact(false);
    }
  };

  const visible = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return corporates.filter((c) =>
      !ql || (c.name || "").toLowerCase().includes(ql) || (c.billing_email || "").toLowerCase().includes(ql)
    );
  }, [corporates, q]);

  return (
    <main className="px-6 md:px-10 py-8" data-testid="platform-corporates-page">
      <header className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
            <Crown className="h-6 w-6 text-amber-600" /> Corporate accounts
          </h1>
          <div className="text-xs text-slate-500">Multi-location parent accounts. Click "Custom price" to set an enterprise rate.</div>
        </div>
        <button
          onClick={load}
          data-testid="platform-refresh-btn"
          className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 transition"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </header>

      <section className="rounded-xl border border-amber-200 bg-amber-50/40 overflow-hidden" data-testid="platform-corporates-section">
        <div className="flex flex-col gap-3 px-5 py-4 border-b border-amber-200/60 md:flex-row md:items-center md:justify-between">
          <div className="relative w-full md:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search corporate or billing email…"
              data-testid="platform-corporate-search"
              className="w-full h-9 rounded-lg border border-slate-200 bg-white pl-8 pr-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none"
            />
          </div>
          <div className="text-xs uppercase tracking-widest text-slate-500">{visible.length} of {corporates.length} corporate{corporates.length === 1 ? "" : "s"}</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="platform-corporates-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-white/70">
                <th className="px-5 py-3">Corporate</th>
                <th className="px-5 py-3">Billing email</th>
                <th className="px-5 py-3">Locations</th>
                <th className="px-5 py-3">Custom price</th>
                <th className="px-5 py-3">Paid until</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 && !loading && (
                <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-500">No corporate accounts match.</td></tr>
              )}
              {visible.map((c) => (
                <tr key={c.id} className={`border-t border-amber-200/40 hover:bg-white ${c.deactivated ? "opacity-60" : ""}`} data-testid={`platform-corporate-${c.id}`}>
                  <td className="px-5 py-3">
                    <div className="font-medium text-slate-900 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => nav(`/platform/corporate/${c.id}`)}
                        data-testid={`platform-corporate-link-${c.id}`}
                        title="View locations & combined payments"
                        className="text-left hover:text-primary hover:underline underline-offset-2 transition"
                      >
                        {c.name}
                      </button>
                      {c.deactivated && (
                        <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-700" data-testid={`corporate-deactivated-pill-${c.id}`}>
                          Deactivated
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 font-mono">{c.id.slice(0, 8)}…</div>
                  </td>
                  <td className="px-5 py-3 text-slate-700">{c.billing_email}</td>
                  <td className="px-5 py-3 text-slate-700">{c.location_count}</td>
                  <td className="px-5 py-3">
                    {c.custom_monthly_price_usd > 0
                      ? <span className="font-semibold text-slate-900">${Number(c.custom_monthly_price_usd).toFixed(0)}/mo</span>
                      : <span className="text-slate-400">Default</span>}
                  </td>
                  <td className="px-5 py-3 text-slate-700 text-sm">
                    {c.paid_until ? new Date(c.paid_until).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <div className="inline-flex items-center gap-1.5">
                      <button
                        onClick={() => setDeactFor(c)}
                        data-testid={`platform-corporate-deact-${c.id}`}
                        title={c.deactivated ? "Reactivate this corporate and all its locations" : "Deactivate this corporate (blocks all logins across its locations)"}
                        className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium transition ${
                          c.deactivated
                            ? "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                            : "border-red-200 bg-white text-red-600 hover:bg-red-50"
                        }`}
                      >
                        {c.deactivated ? <Power className="h-3.5 w-3.5" /> : <Ban className="h-3.5 w-3.5" />}
                      </button>
                      <button
                        onClick={() => openPricing(c)}
                        data-testid={`platform-corporate-pricing-${c.id}`}
                        className="inline-flex items-center gap-1.5 rounded-md border border-slate-900/90 bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800 transition"
                      >
                        <DollarSign className="h-3.5 w-3.5" /> Custom price
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <Dialog open={!!deactFor} onOpenChange={(o) => { if (!o) setDeactFor(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="corporate-deact-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              {deactFor?.deactivated
                ? <Power className="h-5 w-5 text-emerald-600" />
                : <Ban className="h-5 w-5 text-red-600" />}
              {deactFor?.deactivated ? "Reactivate corporate" : "Deactivate corporate"}
            </DialogTitle>
            <DialogDescription>
              {deactFor?.deactivated ? (
                <>Restore <strong>{deactFor?.name}</strong>. All {deactFor?.location_count} location{deactFor?.location_count === 1 ? "" : "s"} and their users will be able to sign in again immediately.</>
              ) : (
                <>Deactivate <strong>{deactFor?.name}</strong>. Every login across all {deactFor?.location_count} location{deactFor?.location_count === 1 ? "" : "s"} — corporate admins, location admins, teachers, officers, kiosk and parents — will be blocked until you reactivate it. No data is deleted.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="pt-2">
            <button
              type="button"
              onClick={() => setDeactFor(null)}
              data-testid="corporate-deact-cancel"
              className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 transition"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={saveDeact}
              disabled={savingDeact}
              data-testid="corporate-deact-confirm"
              className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white transition disabled:opacity-60 ${
                deactFor?.deactivated ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"
              }`}
            >
              {savingDeact && <Loader2 className="h-4 w-4 animate-spin" />}
              {deactFor?.deactivated ? "Reactivate" : "Deactivate"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pricingFor} onOpenChange={(o) => { if (!o) setPricingFor(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="platform-pricing-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-primary" /> {pricingFor?.name} — custom price
            </DialogTitle>
            <DialogDescription>
              Set the monthly price for this corporate account (overrides the ${`${defaultPrice.toFixed(0)}`}/mo per-location default) and optionally extend the paid period.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={savePricing} className="space-y-3 pt-2">
            <label className="block">
              <span className="block mb-1.5 text-xs uppercase tracking-widest text-slate-500">Monthly price (USD)</span>
              <input
                type="number" min="0" step="1"
                value={pricing.price}
                onChange={(e) => setPricing({ ...pricing, price: e.target.value })}
                placeholder="e.g. 499"
                data-testid="platform-pricing-price"
                className="w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none"
              />
              <span className="block mt-1 text-xs text-slate-500">Set to 0 to revert to default per-location pricing.</span>
            </label>
            <label className="block">
              <span className="block mb-1.5 text-xs uppercase tracking-widest text-slate-500">Extend paid period (days)</span>
              <input
                type="number" min="0" step="1"
                value={pricing.days}
                onChange={(e) => setPricing({ ...pricing, days: e.target.value })}
                placeholder="e.g. 30 (one month) or 365 (one year)"
                data-testid="platform-pricing-days"
                className="w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none"
              />
              <span className="block mt-1 text-xs text-slate-500">Adds days on top of the current paid-until date. Leave blank to only update price.</span>
            </label>
            <DialogFooter className="pt-2 gap-2">
              <button type="button" onClick={() => setPricingFor(null)} data-testid="platform-pricing-cancel" className="h-10 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 hover:bg-slate-100">Cancel</button>
              <button type="submit" disabled={savingPricing} data-testid="platform-pricing-save" className="h-10 rounded-lg bg-primary px-4 text-sm font-medium text-white inline-flex items-center gap-2 disabled:opacity-60">
                {savingPricing ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Save
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </main>
  );
}
